/**
 * Tenant display units (TASK-4756).
 *
 * `TenantSettings.cost_unit` and `TenantSettings.volume_unit` are free-text
 * `CharField`s on the backend with **no model `choices`** (defaults `EUR` /
 * `m3`). A tenant can therefore store literally any string, and the dispatcher
 * UI must render whatever is stored without crashing and without silently
 * substituting a different unit.
 *
 * IMPORTANT — these are LABELS, not conversions. Changing `cost_unit` from
 * `EUR` to `PLN` relabels the figures; it does **not** convert them. The stored
 * numbers are whatever the tenant entered, and nothing in this module (or any
 * caller of it) applies an exchange rate or a m³→ft³ factor. Rendering a stored
 * `100` as "100 PLN" is only honest because the tenant declared that their
 * figures are in PLN.
 *
 * The two exported sets below are a FRONTEND decision (the backend constrains
 * nothing). They exist so TASK-4757 can build its `cost_unit` / `volume_unit`
 * selects from a single source instead of re-declaring a list, and so a future
 * third unit is one edit here rather than a sweep of components.
 */

/**
 * Currency codes offered in the settings UI. ISO-4217 alpha-3, matching the
 * region the platform operates in (Belarus + PL/LT/LV) plus USD.
 *
 * NOT exhaustive and NOT enforced: the backend accepts any string, so a tenant
 * may hold a value outside this list (e.g. an older record, or one written via
 * the API). Rendering must always tolerate that — see `isIso4217Code`.
 */
export const SUPPORTED_COST_UNITS = ["EUR", "PLN", "USD", "BYN"] as const;

/**
 * Billing currencies offered anywhere the platform sets a MONEY currency —
 * the dealer's settlement currency, a tenant's billing currency, and the
 * platform default (Sprint 180, TASK-4795).
 *
 * THE single frontend list, and it mirrors the backend's authority:
 * `apps/core/currency.py::SUPPORTED_CURRENCIES` (TASK-4790), which is what
 * `validate_iso4217` enforces on write. Before this constant existed there were
 * TWO frontend lists that disagreed over USD — `["EUR","BYN","PLN"]` in
 * `tenantBillingSettingsSchema.ts` and `["EUR","PLN","BYN","USD"]` in
 * `PlatformSettingsForm.tsx` — so a currency offered on one screen was rejected
 * as invalid on another. Adding a third list for the dealer selector would have
 * made it three; all of them now import this.
 *
 * Distinct from `SUPPORTED_COST_UNITS` above on purpose: that one labels a
 * tenant's free-text *display* unit (`cost_unit`, no backend validation at all),
 * whereas this one is a *validated* billing currency. They happen to hold the
 * same four codes today; they are not the same concept and must not be merged.
 */
export const SUPPORTED_BILLING_CURRENCIES = [
  "EUR",
  "PLN",
  "BYN",
  "USD",
] as const;

export type SupportedBillingCurrency =
  (typeof SUPPORTED_BILLING_CURRENCIES)[number];

/**
 * Volume units offered in the settings UI.
 *
 * Unlike currencies these are pure labels with no `Intl` support at all, so any
 * string renders verbatim next to the number.
 */
export const SUPPORTED_VOLUME_UNITS = ["m3", "ft3", "l"] as const;

export type SupportedCostUnit = (typeof SUPPORTED_COST_UNITS)[number];
export type SupportedVolumeUnit = (typeof SUPPORTED_VOLUME_UNITS)[number];

/** Backend default for `cost_unit` (`settings_app/models.py`). */
export const DEFAULT_COST_UNIT = "EUR";
/** Backend default for `volume_unit` (`settings_app/models.py`). */
export const DEFAULT_VOLUME_UNIT = "m3";

/**
 * Whether `code` is shaped like an ISO-4217 currency code, i.e. whether
 * `Intl.NumberFormat({ style: "currency" })` will accept it.
 *
 * This guard is the whole reason the currency helpers are safe. `Intl` throws a
 * `RangeError` for ANY string that is not exactly three ASCII letters — `"zł"`,
 * `"руб"`, `"EURO"` and `""` all throw. Because `cost_unit` is free text, a
 * tenant typing `zł` into the settings form would otherwise take down every
 * screen that renders a cost (reports, vehicles, order detail) with an
 * uncaught exception rather than showing a slightly odd label.
 *
 * Note this is a SHAPE check, not a validity check: `"XYZ"` passes here and
 * `Intl` accepts it too, echoing it back as the literal code. That is the
 * desired behaviour — an unrecognised code renders verbatim, never as a
 * fallback to EUR, which would reassert exactly the lie this task removes.
 */
export function isIso4217Code(code: string): boolean {
  return /^[A-Za-z]{3}$/.test(code);
}
